import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import {
  Building2,
  Users as UsersIcon,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  ChevronRight,
  AlertTriangle,
  ScrollText,
} from "lucide-react";
import { api } from "../../lib/api";

const STATUS_COLORS = {
  trial: "#8b5cf6",
  active: "#10b981",
  past_due: "#f59e0b",
  suspended: "#ef4444",
  canceled: "#71717a",
};

export default function PlatformOverview() {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [series, setSeries] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, ts, ax] = await Promise.all([
          api.get("/platform/metrics"),
          api.get("/platform/timeseries?days=30"),
          api.get("/platform/audit-log?limit=6"),
        ]);
        setMetrics(m.data);
        setSeries(ts.data.series || []);
        setAudit(ax.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-zinc-200/60" />
        ))}
      </div>
    );
  }

  const statusPie = metrics
    ? [
        { name: "trial", value: metrics.trial, fill: STATUS_COLORS.trial },
        { name: "active", value: metrics.active, fill: STATUS_COLORS.active },
        { name: "past_due", value: metrics.past_due, fill: STATUS_COLORS.past_due },
        { name: "suspended", value: metrics.suspended, fill: STATUS_COLORS.suspended },
        { name: "canceled", value: metrics.canceled, fill: STATUS_COLORS.canceled },
      ].filter((s) => s.value > 0)
    : [];

  const growth =
    series.length >= 2
      ? series[series.length - 1].tenants - series[0].tenants
      : 0;

  return (
    <div className="space-y-6" data-testid="platform-overview">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">
            Serpix Admin
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            {t("platform.overview.title")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t("platform.overview.subtitle")}
          </p>
        </div>
      </header>

      {/* Primary KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t("platform.mrr")}
          value={`$${metrics.mrr_usd.toLocaleString()}`}
          hint={`${metrics.active} active × $19`}
          icon={DollarSign}
          gradient="from-emerald-500 to-teal-500"
          testId="platform-kpi-mrr"
        />
        <KpiCard
          label={t("platform.tenants_label")}
          value={metrics.total_tenants.toLocaleString()}
          hint={
            growth > 0
              ? `+${growth} in 30d`
              : growth < 0
                ? `${growth} in 30d`
                : "flat 30d"
          }
          trend={growth}
          icon={Building2}
          gradient="from-violet-600 to-indigo-600"
          testId="platform-kpi-tenants"
        />
        <KpiCard
          label={t("platform.overview.total_users")}
          value={metrics.total_users.toLocaleString()}
          hint={`${metrics.total_tasks.toLocaleString()} tasks`}
          icon={UsersIcon}
          gradient="from-sky-500 to-blue-600"
          testId="platform-kpi-users"
        />
        <KpiCard
          label={t("platform.conversion")}
          value={`${metrics.trial_to_paid_conversion_pct}%`}
          hint={`Churn ${metrics.churn_pct}%`}
          icon={metrics.churn_pct > metrics.trial_to_paid_conversion_pct ? TrendingDown : TrendingUp}
          gradient="from-fuchsia-500 to-pink-500"
          testId="platform-kpi-conversion"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          className="lg:col-span-2"
          title={t("platform.overview.growth_30d")}
          testId="platform-chart-growth"
        >
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={series} margin={{ left: -10, right: 8, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="gTenants" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gActive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                stroke="#a1a1aa"
                fontSize={11}
              />
              <YAxis stroke="#a1a1aa" fontSize={11} />
              <Tooltip
                contentStyle={{
                  borderRadius: 10,
                  border: "1px solid #e4e4e7",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              />
              <Area
                type="monotone"
                dataKey="tenants"
                stroke="#8b5cf6"
                strokeWidth={2}
                fill="url(#gTenants)"
                name="Total tenants"
              />
              <Area
                type="monotone"
                dataKey="active_tenants"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#gActive)"
                name="Active (paid)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("platform.overview.status_mix")} testId="platform-chart-status">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={statusPie}
                dataKey="value"
                nameKey="name"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
              >
                {statusPie.map((s, i) => (
                  <Cell key={i} fill={s.fill} />
                ))}
              </Pie>
              <Legend
                iconType="circle"
                wrapperStyle={{ fontSize: 11 }}
                formatter={(v) => v}
              />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          className="lg:col-span-2"
          title={t("platform.overview.mrr_trend")}
          testId="platform-chart-mrr"
        >
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={series} margin={{ left: -10, right: 8, top: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                stroke="#a1a1aa"
                fontSize={11}
              />
              <YAxis stroke="#a1a1aa" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{
                  borderRadius: 10,
                  border: "1px solid #e4e4e7",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
                formatter={(v) => `$${v}`}
              />
              <Line
                type="monotone"
                dataKey="mrr_usd"
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "#10b981" }}
                activeDot={{ r: 5 }}
                name="MRR"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title={t("platform.overview.status_bar")}
          testId="platform-chart-status-bar"
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={statusPie}
              margin={{ left: -10, right: 8, top: 8, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#a1a1aa" fontSize={11} />
              <Tooltip
                contentStyle={{
                  borderRadius: 10,
                  border: "1px solid #e4e4e7",
                }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusPie.map((s, i) => (
                  <Cell key={i} fill={s.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Attention row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div
          className="lg:col-span-2 rounded-xl border border-zinc-200 bg-white p-5"
          data-testid="platform-recent-audit"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ScrollText className="h-4 w-4 text-zinc-500" />
              <h3 className="font-semibold text-zinc-900">
                {t("platform.overview.recent_audit")}
              </h3>
            </div>
            <Link
              to="/app/platform/audit"
              className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-900"
            >
              {t("platform.overview.view_all")} <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
          {audit.length === 0 && (
            <p className="rounded-lg bg-zinc-50 py-6 text-center text-sm text-zinc-500">
              {t("platform.overview.no_audit")}
            </p>
          )}
          <ul className="divide-y divide-zinc-100">
            {audit.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-2.5 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                  <Activity className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-zinc-900">
                    <span className="font-mono text-[11px] text-violet-700">
                      {a.action}
                    </span>{" "}
                    by {a.actor_email}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div
          className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5"
          data-testid="platform-attention-card"
        >
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h3 className="font-semibold text-amber-900">
              {t("platform.overview.needs_attention")}
            </h3>
          </div>
          <div className="space-y-2 text-sm">
            <AttentionRow
              label="Past due"
              value={metrics.past_due}
              tone="amber"
              testId="platform-attention-past-due"
            />
            <AttentionRow
              label="Suspended"
              value={metrics.suspended}
              tone="red"
              testId="platform-attention-suspended"
            />
            <AttentionRow
              label="Canceled"
              value={metrics.canceled}
              tone="zinc"
              testId="platform-attention-canceled"
            />
          </div>
          <Link
            to="/app/platform/tenants?filter=past_due"
            className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-900"
          >
            {t("platform.overview.review_tenants")} <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, hint, icon: Icon, gradient, testId, trend }) {
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
      data-testid={testId}
    >
      <div
        className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${gradient} opacity-10 blur-2xl`}
      />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-500">
            {label}
          </div>
          <div className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-950">
            {value}
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">
            {hint}
            {trend !== undefined && trend > 0 && (
              <span className="ml-1 text-emerald-600">↑</span>
            )}
            {trend !== undefined && trend < 0 && (
              <span className="ml-1 text-red-600">↓</span>
            )}
          </div>
        </div>
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} text-white shadow-md`}
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, className = "", testId, children }) {
  return (
    <div
      className={`rounded-xl border border-zinc-200 bg-white p-5 ${className}`}
      data-testid={testId}
    >
      <h3 className="mb-3 text-sm font-semibold text-zinc-900">{title}</h3>
      {children}
    </div>
  );
}

function AttentionRow({ label, value, tone, testId }) {
  const toneMap = {
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800",
    zinc: "bg-zinc-100 text-zinc-700",
  };
  return (
    <div className="flex items-center justify-between rounded-lg bg-white/60 px-3 py-2" data-testid={testId}>
      <span className="text-sm font-medium text-zinc-700">{label}</span>
      <span
        className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-2 text-xs font-semibold ${toneMap[tone] || toneMap.zinc}`}
      >
        {value}
      </span>
    </div>
  );
}
